import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-strength",
  title: "10 Bible verses about strength, and what each one asks",
  h1: "Bible verses about strength",
  description:
    "Ten passages on strength from the World English Bible, with a note on each — including the two that describe strength arriving through weakness rather than instead of it.",
  targets: [
    "bible verses about strength",
    "scripture for strength in hard times",
    "verses for when you feel weak",
  ],
  published: "2026-08-27",
  standfirst:
    "Almost none of these promise that you will feel strong. Several of them describe strength arriving in the middle of not feeling it, which is the more useful claim.",
  takeaways: [
    "Isaiah 40:31 is the passage most people are looking for, and its verb is waiting — the strength described is renewed for those who wait rather than summoned by those who push.",
    "2 Corinthians 12:9 makes the strangest and most quoted claim in the set: power made perfect in weakness, said in answer to a request that was refused three times.",
    "Nehemiah 8:10 locates strength in joy, and it is said to people who had just wept on hearing the law read aloud — the context is a crowd in tears, not a crowd in good spirits.",
    "Read together, the pattern is consistent: strength in scripture is almost always received rather than produced, and usually arrives inside the difficulty rather than before it.",
  ],
  intro: [
    {
      type: "p",
      text: "There are two quite different things people mean by asking for verses about strength. One is capability — get me through this week, this diagnosis, this job. The other is stamina — I have been doing this for two years and I do not think I can keep doing it. Scripture answers both, and it answers them with the same surprising move: the strength is described as coming from outside, and it is described as arriving during rather than before.",
    },
    {
      type: "p",
      text: "That is worth saying at the top because the popular reading of this category runs the other way. Strength verses get printed on sportswear and read as a promise of capability at the moment of effort. The passages themselves are more often about waiting, about weakness, and about being sustained by something that is not yours — which is why they still work at the two-year mark, when effort has stopped being the problem.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain. Trimmed passages are marked with an ellipsis.",
    },
  ],
  verses: [
    {
      verse: {
        text: "But those who wait for Yahweh will renew their strength. They will mount up with wings like eagles. They will run, and not be weary. They will walk, and not faint.",
        ref: "Isaiah 40:31",
      },
      note: "The verse the whole category is built around, and its central verb is the one nobody quotes: wait. The passage before it says that even young men grow weary and fall exhausted — the point being made is that natural strength runs out on schedule and that the renewal described is a different supply. Note also the order of the images, which descends: soaring, running, then walking. It ends with the least dramatic and most necessary one.",
    },
    {
      verse: {
        text: "He has said to me, “My grace is sufficient for you, for my power is made perfect in weakness.”",
        ref: "2 Corinthians 12:9",
      },
      note: "This is the answer to a prayer that was turned down. Paul says he asked three times for something to be taken away and it was not; this sentence is what he got instead. That setting is the whole meaning of it — the claim is not that weakness is pleasant or that the problem was secretly good, but that the power in question operates through the unresolved thing rather than after it has been removed.",
    },
    {
      verse: {
        text: "God is our refuge and strength, a very present help in trouble.",
        ref: "Psalm 46:1",
      },
      note: "Three words carry it: very present. Not available, not eventually — present, in the trouble, now. The psalm goes on to describe mountains falling into the sea and waters roaring, which is to say it is not imagining a mild difficulty, and it is the same psalm that ends with be still and know that I am God.",
    },
    {
      verse: {
        text: "Don't be grieved, for the joy of Yahweh is your strength.",
        ref: "Nehemiah 8:10",
      },
      note: "Said to a crowd who had just heard the law read aloud for the first time in generations and had begun to weep. The instruction is not to cheer up in the abstract; it is said to people in tears, on a day of enormous significance, and it locates their capacity to keep going in something outside their own mood. The same verse tells them to go and eat, and to send portions to anyone with nothing prepared.",
    },
    {
      verse: {
        text: "My flesh and my heart fails, but God is the strength of my heart and my portion forever.",
        ref: "Psalm 73:26",
      },
      note: "The most honest sentence in the collection, because it concedes the failure in the same breath. The psalmist does not say he will not fail; he says he has, and then names what remains. Psalm 73 as a whole is a man admitting he nearly gave up over how well things were going for people he considered dishonest, which makes it an unusually contemporary piece of writing.",
    },
    {
      verse: {
        text: "Finally, be strong in the Lord, and in the strength of his might.",
        ref: "Ephesians 6:10",
      },
      note: "The opening of the armour passage, and the preposition is doing the work: strong IN the Lord and in HIS might, not strong for him or by your own. Everything that follows in the chapter is equipment being put on rather than muscle being developed, which is a deliberate picture — the soldier's strength is largely a matter of what they are wearing.",
    },
    {
      verse: {
        text: "Yah is my strength and song. He has become my salvation.",
        ref: "Exodus 15:2",
      },
      note: "From the song sung on the far side of the sea, immediately after an escape nobody had contributed to. Strength and song in the same clause is worth noticing: the natural response to having been carried is not a resolution but music, and the oldest poetry in the Bible treats those two words as belonging together.",
    },
    {
      verse: {
        text: "Yahweh, the Lord, is my strength. He makes my feet like deer's feet, and enables me to go in high places.",
        ref: "Habakkuk 3:19",
      },
      note: "The last line of a book that spends three chapters complaining, and the verses just before it are a list of everything failing: no figs, no fruit, no flock in the fold. The strength claimed here is claimed with the harvest gone, which is what makes it a serious sentence rather than a poster.",
    },
    {
      verse: {
        text: "Yahweh is my strength and my shield. My heart has trusted in him, and I am helped.",
        ref: "Psalm 28:7",
      },
      note: "Strength paired with shield — the two halves of what a person under pressure actually needs, which is the capacity to keep going and something between them and the next blow. The perfect tense in the second half is the interesting part: I am helped, reported as something that has already happened rather than requested.",
    },
    {
      verse: {
        text: "I can do all things through Christ, who strengthens me.",
        ref: "Philippians 4:13",
      },
      note: "Included because everybody expects it and because the sentence before it changes what it means. Verse 12 lists the all things: being filled and being hungry, abounding and being in need. It is a claim about handling both ends of a circumstance, written from custody, and not a claim about achieving whatever is attempted.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "which-one-to-take",
      text: "Which one to take",
    },
    {
      type: "p",
      text: "If the problem is this week, Psalm 46:1 and Ephesians 6:10 are the two to hold — present help, and equipment rather than muscle. If the problem is the second year of something, Isaiah 40:31 and Psalm 73:26 are the ones written for it: one has waiting as its verb, and the other admits the failure out loud before naming what is left.",
    },
    {
      type: "p",
      text: "And if you have prayed for a thing to be removed and it has not been, 2 Corinthians 12:9 is the passage in this list that was produced by exactly that situation. Read the whole paragraph rather than the sentence; the refusal is part of it. Philippians 4:13 has a page of its own — {what-the-all-things-actually-are} — because the popular reading of it is close to the reverse of the letter's.",
      links: { "what-the-all-things-actually-are": "/verse/philippians-4-13" },
    },
    {
      type: "h2",
      id: "a-prayer-for-strength",
      text: "A prayer from these passages",
    },
    {
      type: "p",
      text: "Lord, my heart fails and you are the strength of my heart. I am not asking to feel strong. I am asking to be helped in the middle of this, as a very present help, and to keep walking when the running is over. Let your power be made perfect in the part of me that is not coping. Amen.",
    },
    {
      type: "note",
      text: "Note how many of these were written after something had already gone wrong. That is not a coincidence, and it is why they hold.",
    },
  ],
  faq: [
    {
      q: "What is the best Bible verse for strength?",
      a: "Isaiah 40:31 is the one most people mean, and it rewards a slow reading: the strength is renewed for those who wait, and the images descend from soaring to running to walking. If your situation is a long one rather than a hard week, Psalm 73:26 is the more honest companion to it.",
    },
    {
      q: "Does the Bible say God gives strength when we are weak?",
      a: "2 Corinthians 12:9 says power is made perfect in weakness, and says it as the answer to a request refused three times. Psalm 73:26 says flesh and heart fail, then names God as the strength of the heart. The pattern is strength received inside the difficulty rather than issued before it.",
    },
    {
      q: "Which translation are these from?",
      a: "The World English Bible, which is in the public domain, so each passage can be printed whole. Wording varies between translations; none of the notes above depends on a particular rendering.",
    },
  ],
  related: [
    "morning-quiet-time-routine",
    "how-to-memorize-bible-verses",
    "digital-sabbath",
  ],
  relatedTopics: ["bible-verses-about-peace", "bible-verses-about-rest"],
};
